import React, { useState, useEffect } from 'react';
import { Pencil, Trash2, Search, Shield, Plus, X } from "lucide-react";
import Loading from '../../component/Loading/Loading';
import AlertMsg from '../../component/AlertMsg/AlertMsg';
import { useSelector } from "react-redux";
import useAxiosInstance from '../../services/useAxiosInstance';
import GlassmorphicModal from "../../component/ReusableComponents/GlassmorphicModal";
import Pagination from '../../utils/Pagenation/Pagenation';

const RolePageCrud = () => {
    const axiosInstance = useAxiosInstance();
    const [selectedContact, setSelectedContact] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [loading, setLoading] = useState(true);
    const [rolePages, setRolePages] = useState([]);
    const [roleIds, setRoleIds] = useState(0);
    const [showFormModal, setShowFormModal] = useState(false);
    const [alert, setAlert] = useState({ visible: false, message: '', type: '' });
    const communityId = useSelector((state) => state.auth.communityId);
    const userId = useSelector((state) => state.auth.userId);

    const [formData, setFormData] = useState({
        create: true,
        delete: true,
        pageid: 0,
        read: true,
        roleid: 0,
        rolepageid: 0,
        update: true
    });
    const [currentPage, setCurrentPage] = useState(0);
    const itemsPerPage = 5;

    const [roles, setRoles] = useState([]);
    const [pages, setPages] = useState([]);

    useEffect(() => {
        const fetchRoles = async () => {
            setLoading(true);
            try {
                const response = await axiosInstance.get(`/api/public/role/getRoles`);
                setRoles(response.data);
            } catch (error) {
                console.error('Error fetching roles', error);
            } finally {
                setLoading(false);
            }
        };

        const fetchPages = async () => {
            setLoading(true);
            try {
                const response = await axiosInstance.get(`/api/public/page/getAll`);
                setPages(response.data);
            } catch (error) {
                console.error('Error fetching pages', error);
            } finally {
                setLoading(false);
            }
        };

        fetchRoles();
        fetchPages();
    }, []);

    useEffect(() => {
        fetchRolePageData();
    }, [roleIds]);

    const fetchRolePageData = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get(
                `/api/public/rolepage/getByRoleId/${roleIds}`,
                {
                    params: { roleId: roleIds }
                }
            );
            setRolePages(response.data);
        } catch (error) {
            console.error('Error fetching role data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (deleteId) => {
        setLoading(true);
        try {
            await axiosInstance.delete(`/api/public/rolepage/delete/${deleteId}`);
            setShowDeleteModal(false);
            setAlert({ visible: true, message: 'Role page deleted successfully!', type: 'success' });
            fetchRolePageData();
        } catch (error) {
            setAlert({ visible: true, message: 'Error deleting role page: Please try again.', type: 'error' });
            console.error("Error deleting contact:", error);
        } finally {
            setLoading(false);
        }
    };

    const [deleteId, setDeleteId] = useState(0);

    const openDeleteModal = (rolePageId) => {
        setDeleteId(rolePageId);
        setShowDeleteModal(true);
    };

    const openViewModal = (contact) => {
        setSelectedContact(contact);
        setShowViewModal(true);
    };

    const openFormModal = () => {
        setFormData({
            create: true,
            delete: true,
            pageid: 0,
            read: true,
            roleid: 0,
            rolepageid: 0,
            update: true
        });
        setShowFormModal(true);
    };

    const closeModal = () => {
        setShowDeleteModal(false);
        setShowViewModal(false);
        setShowFormModal(false);
    };

    const [searchName, setSearchName] = useState("");
    const handleSearchChange = (event) => {
        setSearchName(event.target.value);
    };

    const handleEdit = (rolePage) => {
        setFormData({
            create: rolePage.create,
            delete: rolePage.delete,
            pageid: rolePage.pageId,
            read: rolePage.read,
            roleid: rolePage.roleId,
            rolepageid: rolePage.rolePageId,
            update: rolePage.update
        });
        console.log(rolePage);
        console.log(rolePage.pageName);

        setShowFormModal(true);
    };

    const filteredData = rolePages.filter((request) =>
        request.pageName?.toLowerCase().includes(searchName.toLowerCase())
    );

    const pageCount = Math.ceil(filteredData.length / itemsPerPage);
    const mergedFilteredData = filteredData.slice(currentPage * itemsPerPage, (currentPage + 1) * itemsPerPage);

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        const apiUrl = formData.rolepageid === 0
            ? `/api/public/rolepage/add`
            : `/api/public/rolepage/update`;

        try {
            await axiosInstance.post(apiUrl, formData);
            setShowFormModal(false);
            fetchRolePageData();
            setAlert({ visible: true, message: 'Role page added successfully!', type: 'success' });
        } catch (error) {
            setAlert({ visible: true, message: 'Error adding role page: Please try again.', type: 'error' });
            console.error('Error saving role page:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value
        });
    };

    // Form modal content
    const formModalContent = (
        <form onSubmit={handleFormSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Page Name</label>
                    <select
                        name="pageid"
                        value={formData.pageid}
                        onChange={handleInputChange}
                        required
                        className="w-full rounded-md border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="">Select Page</option>
                        {pages.map((page) => (
                            <option key={page.pageid} value={page.pageid}>
                                {page.name}
                            </option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                    <select
                        name="roleid"
                        value={formData.roleid}
                        onChange={handleInputChange}
                        required
                        className="w-full rounded-md border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="">Select Role</option>
                        {roles.map((role) => (
                            <option key={role.roleid} value={role.roleid}>
                                {role.name}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
        </form>
    );

    // Form modal footer with action buttons
    const formModalFooter = (
        <div className="flex justify-end space-x-2">
            <button
                type="button"
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                onClick={closeModal}
            >
                Cancel
            </button>
            <button
                type="button"
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                onClick={handleFormSubmit}
            >
                Save
            </button>
        </div>
    );

    // Delete modal footer with action buttons
    const deleteModalFooter = (
        <div className="flex justify-end space-x-2">
            <button
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                onClick={closeModal}
            >
                Cancel
            </button>
            <button
                className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
                onClick={() => handleDelete(deleteId)}
            >
                Delete
            </button>
        </div>
    );

    return (
        <div className="bg-gray-50 min-h-screen md:p-4">
            {loading && <Loading />}
            {!loading && (
                <div className="max-w-7xl mx-auto">
                    {alert.visible && (
                        <AlertMsg
                            message={alert.message}
                            type={alert.type}
                            onClose={() => setAlert({ ...alert, visible: false })}
                        />
                    )}

                    <div className="flex justify-between items-center mb-6">
                        <h1 className="text-2xl font-bold text-gray-800">Role Page Management</h1>
                        <button
                            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md flex items-center gap-2"
                            onClick={openFormModal}
                        >
                            <Plus size={18} />
                            <span>Add Role Page</span>
                        </button>
                    </div>

                    <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-6">
                        <div className="p-4 flex flex-col md:flex-row justify-between items-center border-b border-gray-200">
                            <div className="relative w-full md:w-64 mb-4 md:mb-0">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Search className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    type="text"
                                    id="searchName"
                                    value={searchName}
                                    onChange={handleSearchChange}
                                    placeholder="Search by page name"
                                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-md w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>

                            <div className="flex items-center">
                                <div className="relative">
                                    <div className="flex items-center">
                                        <Shield className="h-5 w-5 text-gray-400 mr-2" />
                                        <select
                                            id="roleids"
                                            value={roleIds}
                                            onChange={(e) => setRoleIds(e.target.value)}
                                            className="px-3 py-[11px] border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                                        >
                                            <option value="">Select Role</option>
                                            {roles.map((role) => (
                                                <option key={role.roleid} value={role.roleid}>
                                                    {role.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-blue-400">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-sm font-medium text-white tracking-wider">Page Name</th>
                                        <th className="px-6 py-3 text-center text-sm font-medium text-white tracking-wider">Display Order</th>
                                        <th className="px-6 py-3 text-left text-sm font-medium text-white tracking-wider">Allow Role</th>
                                        <th className="px-6 py-3 text-center text-sm font-medium text-white tracking-wider">Action</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {mergedFilteredData.length === 0 ? (
                                        <tr>
                                            <td colSpan="4" className="px-6 py-4 text-center text-sm text-gray-500">No Data Found</td>
                                        </tr>
                                    ) : (
                                        mergedFilteredData.map((contact, index) => (
                                            <tr key={index} className={index % 2 === 0 ? "bg-gray-50" : "bg-white"}>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{contact.pageName}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">{contact.displayOrder}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{contact.roleName}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium flex justify-center gap-3">
                                                    <button
                                                        className="text-gray-500 hover:text-gray-700"
                                                        onClick={() => handleEdit(contact)}
                                                        title="Edit Role Page"
                                                    >
                                                        <Pencil size={18} />
                                                    </button>
                                                    <button
                                                        className="text-red-500 hover:text-red-700"
                                                        onClick={() => openDeleteModal(contact.rolePageId)}
                                                        title="Delete Role Page"
                                                    >
                                                        <Trash2 size={18} />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <div className="bg-gray-50 px-6 py-3 flex items-center justify-between border-t border-gray-200">
                            <div className="text-sm text-gray-700">
                                Page {currentPage + 1} of {pageCount || 1}
                            </div>
                            <div className="flex-1 flex justify-end">
                                <Pagination
                                    currentPage={currentPage}
                                    pageCount={pageCount || 1}
                                    setCurrentPage={setCurrentPage}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Form Modal using GlassmorphicModal */}
            <GlassmorphicModal
                isOpen={showFormModal}
                onClose={closeModal}
                title={formData.rolepageid === 0 ? "Add Role Page" : "Edit Role Page"}
                size="md"
                footer={formModalFooter}
            >
                {formModalContent}
            </GlassmorphicModal>

            {/* Delete Modal using GlassmorphicModal */}
            <GlassmorphicModal
                isOpen={showDeleteModal}
                onClose={closeModal}
                title="Confirm Delete"
                size="sm"
                footer={deleteModalFooter}
            >
                <p className="text-gray-700">
                    Are you sure you want to delete this role page? This action cannot be undone.
                </p>
            </GlassmorphicModal>
        </div>
    );
};

export default RolePageCrud;